import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getGems, getUsers, awardGems } from "../services/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Gem, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });

function gemsTitle(role, location) {
  if (role === "Employee") return "My Gems";
  if (role === "Manager") return `${location} Gems Activity`;
  return "Gems Management";
}

export default function GemsPage() {
  const { user, refresh } = useAuth();
  const canManage = user.role === "HR" || user.role === "Director";
  const [txns, setTxns] = useState([]);
  const [team, setTeam] = useState([]);

  const [target, setTarget] = useState("");
  const [amt, setAmt] = useState("");
  const [reason, setReason] = useState("");

  const load = () => getGems().then(setTxns).catch(() => {});
  useEffect(() => {
    load();
    if (canManage) getUsers().then(setTeam).catch(() => {});
  }, [canManage]);

  const award = async () => {
    if (!target) { toast.error("Select an employee."); return; }
    if (!amt || Number(amt) === 0) { toast.error("Enter an amount."); return; }
    try {
      await awardGems(target, Number(amt), reason || "Gems awarded");
      setAmt(""); setReason(""); setTarget("");
      await load();
      await refresh();
      toast.success("Gems awarded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not award gems.");
    }
  };

  const title = gemsTitle(user.role, user.primaryLocation);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-panel/50">Rewards</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-vault-panel mt-1">{title}</h1>
      </div>

      {user.role === "Employee" && (
        <div className="rounded-3xl bg-vault-accent text-vault-panel p-7 flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-vault-panel/70">Current Balance</div>
            <div className="font-display text-5xl font-extrabold mt-2">{user.gemsBalance}</div>
          </div>
          <Gem className="h-16 w-16 text-vault-panel/30" />
        </div>
      )}

      {canManage && (
        <div className="rounded-3xl bg-vault-panel p-6">
          <h3 className="font-display text-lg font-bold text-vault-cream mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-vault-accent" /> Award Gems</h3>
          <div className="grid sm:grid-cols-4 gap-3 items-end">
            <div className="space-y-2 sm:col-span-2">
              <Label className="text-vault-subtle">Employee</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="bg-vault-panel2 border-white/10 text-vault-cream h-11"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{team.map((u) => <SelectItem key={u.id} value={u.id}>{u.firstName} {u.lastName} \u00b7 {u.primaryLocation}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Amount</Label>
              <Input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" placeholder="50" />
            </div>
            <button onClick={award} className="rounded-2xl bg-vault-accent text-vault-panel font-bold h-11 px-5 hover:bg-vault-accentdark transition-colors">Award</button>
            <div className="space-y-2 sm:col-span-4">
              <Label className="text-vault-subtle">Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} className="bg-vault-panel2 border-white/10 text-vault-cream h-11" placeholder="e.g. Outstanding contribution" />
            </div>
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-vault-panel p-2 sm:p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="text-left text-vault-subtle text-xs uppercase tracking-wider">
                {user.role !== "Employee" && <th className="py-3 px-3 font-semibold">Employee</th>}
                <th className="py-3 px-3 font-semibold">Type</th>
                <th className="py-3 px-3 font-semibold">Reason</th>
                <th className="py-3 px-3 font-semibold">Date</th>
                <th className="py-3 px-3 font-semibold text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 ? (
                <tr><td colSpan={5} className="py-10 text-center text-vault-subtle">No gem transactions yet.</td></tr>
              ) : txns.map((t) => (
                <tr key={t.id} className="border-t border-white/5">
                  {user.role !== "Employee" && <td className="py-3.5 px-3 text-vault-cream font-medium">{t.userName}</td>}
                  <td className="py-3.5 px-3 text-vault-subtle">{t.transactionType}</td>
                  <td className="py-3.5 px-3 text-vault-subtle max-w-[260px] truncate">{t.reason}</td>
                  <td className="py-3.5 px-3 text-vault-subtle">{fmt(t.createdAt)}</td>
                  <td className="py-3.5 px-3 text-right">
                    <span className={`inline-flex items-center gap-1 font-display font-bold ${t.amount >= 0 ? "text-vault-accent" : "text-red-400"}`}>
                      {t.amount >= 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                      {t.amount >= 0 ? "+" : ""}{t.amount}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
